import type { ChannelNotificationMode } from '@/convex/organizations/channels/notification_policy.helpers';
import {
  shouldToastChannelActivity,
  type ChannelNotificationCounts,
} from './channel-preference-state';

/** One chat's counters as `listActivitySignals` returns them. */
export type ActivitySignal = {
  channelId: string;
  kind: 'public' | 'private' | 'dm';
  name: string;
  unreadCount: number;
  mentionCount: number;
  mentionOnly: boolean;
  lastMentionAuthorName: string | null;
  lastAuthorName: string | null;
  notificationMode: ChannelNotificationMode;
  mutedUntil: number | null;
};

export type ActivityToast = {
  channelId: string;
  /** `mention`: new @mention; `dm`: new direct message; `message`: new post. */
  kind: 'mention' | 'dm' | 'message';
  /** The person who wrote it (null when unknown, e.g. imai). */
  authorName: string | null;
  /** `#name` for channels, the group name for named group DMs, else null. */
  channelLabel: string | null;
};

export type ActivityBaseline = Map<string, ChannelNotificationCounts>;

export function activityBaseline(
  signals: readonly ActivitySignal[]
): ActivityBaseline {
  return new Map(
    signals.map((signal) => [
      signal.channelId,
      { mentionCount: signal.mentionCount, unreadCount: signal.unreadCount },
    ])
  );
}

function channelLabel(signal: ActivitySignal): string | null {
  if (signal.kind !== 'dm') return `#${signal.name}`;
  return signal.name.trim() || null;
}

/**
 * Toasts for counters that grew since the previous snapshot. A DM message is
 * personal, so it toasts like a mention; channel posts follow the viewer's
 * notification mode. The open chat, muted chats and first sightings (no
 * previous counters) never toast.
 */
export function diffActivitySignals(
  previous: ActivityBaseline,
  signals: readonly ActivitySignal[],
  options: { activeChannelId: string; now: number }
): ActivityToast[] {
  const toasts: ActivityToast[] = [];
  for (const signal of signals) {
    const before = previous.get(signal.channelId);
    if (!before) continue;
    const isDm = signal.kind === 'dm';
    const mentioned = signal.mentionCount > before.mentionCount;
    const posted = signal.unreadCount > before.unreadCount;
    const toast = shouldToastChannelActivity({
      activeChannelId: options.activeChannelId,
      channelId: signal.channelId,
      // A new DM counts as a mention for the notification-mode gate.
      current: {
        mentionCount:
          isDm && posted ? before.mentionCount + 1 : signal.mentionCount,
        unreadCount: signal.unreadCount,
      },
      previous: before,
      notificationMode: signal.notificationMode,
      mutedUntil: signal.mutedUntil,
      now: options.now,
    });
    if (!toast) continue;
    toasts.push({
      channelId: signal.channelId,
      kind: mentioned ? 'mention' : isDm ? 'dm' : 'message',
      authorName: mentioned
        ? signal.lastMentionAuthorName
        : signal.lastAuthorName,
      channelLabel: channelLabel(signal),
    });
  }
  return toasts;
}

type ToastCopy = {
  mention: (name: string, channel: string) => string;
  mentionDm: (name: string) => string;
  mentionAnon: (channel: string) => string;
  dm: (name: string) => string;
  message: (name: string, channel: string) => string;
  /** Fallback "New message in {chat}". */
  newMessage: (chat: string) => string;
  /** Label for a chat with no name of its own (an unnamed DM). */
  fallbackChat: string;
};

/** The toast line: always names the author when known, never the channel. */
export function activityToastText(
  toast: ActivityToast,
  copy: ToastCopy
): string {
  const chat = toast.channelLabel ?? toast.authorName ?? copy.fallbackChat;
  if (toast.kind === 'mention') {
    if (!toast.authorName) return copy.mentionAnon(chat);
    return toast.channelLabel?.startsWith('#')
      ? copy.mention(toast.authorName, toast.channelLabel)
      : copy.mentionDm(toast.authorName);
  }
  if (toast.kind === 'dm') {
    return toast.authorName ? copy.dm(toast.authorName) : copy.newMessage(chat);
  }
  return toast.authorName && toast.channelLabel
    ? copy.message(toast.authorName, toast.channelLabel)
    : copy.newMessage(chat);
}
